<script setup lang="ts">
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { ServiceError } from "@core/exceptions"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type { Services } from "@services"
import type { ThresholdPoint } from "@modules/energy-manager/types/alarme"

const props = withDefaults(
    defineProps<{
        type: "building" | "source"
        itemId: string
        variable: string
        /** État de persistance du profil importé (parent). */
        saving?: boolean
    }>(),
    {
        saving: false,
    }
)

const emit = defineEmits<{
    cancel: []
    save: []
    "change-points": [points: ThresholdPoint[]]
}>()

const services = inject<Services>("services")!

const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                importTitle: "Importer un profil",
                importHintBuilding:
                    "Sélectionnez un autre bâtiment pour importer son profil de seuils pour cette variable.",
                importHintSource:
                    "Sélectionnez un autre capteur ou compteur pour importer son profil de seuils pour cette variable.",
                importSelectPlaceholder: "Choisir…",
                import: "Importer",
                cancel: "Annuler",
                importListEmptyBuilding:
                    "Aucun autre bâtiment avec un profil pour cette variable.",
                importListEmptySource:
                    "Aucun autre capteur ou compteur avec un profil pour cette variable.",
                importListError:
                    "Impossible de charger la liste des profils disponibles.",
            },
        },
    },
})

/** Élément renvoyé par GET /v5/building|source/profil/:variable (profil partiel). */
type ProfileListEntry = {
    _id: string
    /** Identifiant métier source (routes widget) ; préféré pour comparer à `itemId`. */
    _id_source?: string
    name?: string
    profil?: Record<string, ThresholdPoint[]>
    adress?: { street?: string; zipcode?: string; city?: string }
    idtype?: string
    idsource?: string
}

function normalizeId(raw: unknown): string {
    if (raw == null) return ""
    return typeof raw === "object" && raw !== null && "$oid" in raw
        ? String((raw as { $oid: string }).$oid)
        : String(raw)
}

function formatAddress(adress?: {
    street?: string
    zipcode?: string
    city?: string
}): string {
    if (!adress) return ""
    const parts = [
        adress.street ? `${adress.street},` : "",
        adress.zipcode ?? "",
        adress.city ?? "",
    ].filter(Boolean)
    return parts.join(" ").trim()
}

function normalizeProfileList(data: unknown): ProfileListEntry[] {
    if (!Array.isArray(data)) return []
    return data.map((raw) => {
        const o = raw as Record<string, unknown>
        const _id = normalizeId(o._id)
        const _id_source =
            o._id_source != null ? String(o._id_source) : undefined
        return {
            _id,
            _id_source,
            name: o.name as string | undefined,
            profil: o.profil as Record<string, ThresholdPoint[]> | undefined,
            adress: o.adress as ProfileListEntry["adress"],
            idtype: o.idtype as string | undefined,
            idsource: o.idsource as string | undefined,
        }
    })
}

/** Exclut le bâtiment / la source en cours (clé route vs document API). */
function isCurrentEntity(row: ProfileListEntry): boolean {
    if (props.type === "building") {
        return row._id === props.itemId
    }
    const sourceKey = row._id_source ?? row._id
    return String(sourceKey) === String(props.itemId)
}

const profileFetch = ref<ProfileListEntry[]>([])
const selectedImportedId = ref<string | null>(null)

const selectOptions = computed(() =>
    profileFetch.value
        .filter((row) => row._id && !isCurrentEntity(row))
        .map((row) => ({
            label: formatSelectLabel(row),
            value: row._id,
        }))
)

function formatSelectLabel(row: ProfileListEntry): string {
    if (props.type === "building") {
        const baseName = row.name?.trim() ? row.name : "-"
        const addr = formatAddress(row.adress)
        return addr ? `${baseName} — ${addr}` : baseName
    }
    if (row.name?.trim()) {
        return `${row.name} — ${row.idtype ?? ""} : ${row.idsource ?? ""}`
    }
    return `${row.idtype ?? ""} : ${row.idsource ?? ""}`
}

const {
    fetch: loadProfileSources,
    isFetching: isLoadingProfiles,
    error: loadProfilesError,
} = useFetchWrapper(async () => {
    const raw =
        props.type === "building"
            ? await services.$getBuildingsByProfile(props.variable)
            : await services.$getSourcesByProfile(props.variable)
    profileFetch.value = normalizeProfileList(raw)
    selectedImportedId.value = null
})

const loadErrorMessage = computed(() => {
    const err = loadProfilesError.value
    if (!err) return ""
    return err instanceof ServiceError
        ? err.message
        : t("alarm.importListError")
})

watch(
    () => [props.type, props.itemId, props.variable] as const,
    () => {
        void loadProfileSources()
    },
    { immediate: true }
)

function onSelectImportedId(id: string | null) {
    selectedImportedId.value = id
    if (!id) {
        return
    }
    const row = profileFetch.value.find((r) => r._id === id)
    const points = row?.profil?.[props.variable] ?? []
    emit("change-points", Array.isArray(points) ? [...points] : [])
}

const getCanApplyImport = computed(() => {
    if (!selectedImportedId.value) return false
    const row = profileFetch.value.find(
        (r) => r._id === selectedImportedId.value
    )
    const pts = row?.profil?.[props.variable]
    return Array.isArray(pts) && pts.length > 0
})
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
            <h4 class="text-base font-semibold text-primary">
                {{ t("alarm.importTitle") }}
            </h4>
            <p class="text-sm text-slate-600">
                {{
                    type === "building"
                        ? t("alarm.importHintBuilding")
                        : t("alarm.importHintSource")
                }}
            </p>
        </div>

        <BaseMessage v-if="loadErrorMessage" severity="error">
            {{ loadErrorMessage }}
        </BaseMessage>

        <BaseSkeleton
            v-else-if="isLoadingProfiles"
            width="100%"
            height="2.75rem"
        />

        <div v-else class="flex flex-col gap-2">
            <BaseSelect
                :model-value="selectedImportedId"
                :options="selectOptions"
                option-label="label"
                option-value="value"
                :placeholder="t('alarm.importSelectPlaceholder')"
                :disabled="selectOptions.length === 0"
                class="w-full"
                show-clear
                @update:model-value="
                    onSelectImportedId($event as string | null)
                "
            />
            <BaseMessage
                v-if="selectOptions.length === 0"
                severity="warn"
                size="small"
            >
                {{
                    type === "building"
                        ? t("alarm.importListEmptyBuilding")
                        : t("alarm.importListEmptySource")
                }}
            </BaseMessage>
        </div>

        <div class="flex flex-wrap gap-2">
            <BaseButton
                :loading="props.saving"
                :disabled="
                    !getCanApplyImport || isLoadingProfiles || props.saving
                "
                :label="t('alarm.import')"
                @click="emit('save')"
            />
            <BaseButton
                outlined
                severity="secondary"
                :disabled="props.saving"
                :label="t('alarm.cancel')"
                @click="emit('cancel')"
            />
        </div>
    </div>
</template>
